import { useState, type FormEvent } from 'react';

import { ApiError, api, type Session } from './api';

export function Login({ onSignedIn }: { onSignedIn: (session: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(email, password);
      // Permissions come from /auth/me rather than the login response, so the
      // whole app reads them from one place regardless of how the session began.
      onSignedIn(await api.me());
    } catch (cause) {
      /*
       * The server returns one message for a wrong password, an unknown address,
       * and a deactivated account, so that a stranger cannot use the login form to
       * discover who has an account. Passing it through verbatim keeps that
       * property; inventing a friendlier message here would break it.
       */
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Could not reach the server. Check that the API is running.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            'radial-gradient(60rem 40rem at 20% -10%, oklch(0.92 0.04 235 / 0.4), transparent 60%),' +
            'radial-gradient(50rem 34rem at 90% 10%, oklch(0.93 0.04 165 / 0.3), transparent 60%)',
        }}
      />
      <form
        onSubmit={submit}
        className="rise relative w-full max-w-sm rounded-2xl border border-edge bg-surface-raised p-7 shadow-lg"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-profit">
          Staffing Platform
        </p>
        <h1 className="mt-2 text-xl font-semibold text-ink">Sign in</h1>
        <p className="mt-1 text-sm text-ink-faint">
          Gross profit, commission, and payroll staging.
        </p>

        <label className="mt-6 block text-xs font-medium text-ink-muted" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="mt-1.5 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-revenue-dim"
        />

        <label className="mt-4 block text-xs font-medium text-ink-muted" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="mt-1.5 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-revenue-dim"
        />

        {error !== null && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-caution-dim/60 bg-caution-dim/10 px-3 py-2 text-xs text-caution"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-lg bg-profit px-3 py-2 text-sm font-semibold text-surface-base transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}
